import { ContractState, ContractViewModel, QuoteReason } from '@ail/contracts-api'
import { CustomerIndexViewModel } from '@ail/customers-api'
import { OrderType, VehicleOrderStatus, VehicleOrderViewModel } from '@ail/orders-api'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink, RouteComponentProps } from 'react-router-dom'
import { Arr } from '../../helpers/Arr'
import { FrontendSettings } from '../../helpers/FrontendSettings'
import { getFormTitle, getVehicleOrderStatus } from '../../helpers/Orders'
import { RequestExportToPDF } from '../../helpers/RequestExportToPDF'
import { defineContractStateNew, hasTimeElapsed, isDateValid, printDate } from '../../helpers/Utils'
import { useFilteredCustomers } from '../../hooks/useFilteredCustomers'
import { useSettings } from '../../hooks/useSettings'
import { useGetContractsBySecurityKeysQuery } from '../../redux/api/contractsApi'
import { useGetAllUnitsQuery, useGetTenantsQuery } from '../../redux/api/customerApi'
import { useGetOrdersFormsQuery, useGetVehicleOrdersQuery, useLazyGetOrderQuery } from '../../redux/api/ordersApi'
import { useAppSelector } from '../../redux/store'
import { productTypeIdMap } from '../../types/constants'
import { DriverFilter, FrontendSettingsKeys } from '../../types/enums'
import { CellExpanderFormatter } from '../GeneralContracts/Formatters/CellExpanderFormatter'
import OrderDetails from '../GeneralContracts/Formatters/OrderDetails'
import Alert from '../common/Alert/Alert'
import ConfirmedDataTooltip from '../common/ConfirmedDataTooltip/ConfirmedDataTooltip'
import {
  Grid,
  GridColumn,
  InlineButtonFilter,
  InlineTextFilter,
  ModalContractsDropdownFilter,
  ModalDriverFilter,
} from '../common/Grid/Grid'
import { LoadingSpinner } from '../common/LoadingSpinner/LoadingSpinner'
import { SchedulerModal } from '../common/Modals/SchedulerModal/SchedulerModal'
import './Delivery.scss'
import { ContractCostCenterCell } from './GridCells/CostCenterCell/CostCenterCell'
import ObjTypeCell from './GridCells/ObjectTypeCell/ObjectTypeCell'
import Returns from './Returns/Returns'
import { CombinedScheduler } from './SchedulerModals/CombinedScheduler'
import { OrderWarningCount } from '../common/WarningCount/WarningCount'
import { ColKeysEnum } from '../GeneralContracts/GeneralContractsEnums'
import { useContractSupport } from '../../hooks/useContractSupport'
import { Orders } from '../../utils/orders'
import { useIsInterfleetPro } from '../../hooks/useIsInterfleetPro'
import { Loader } from '../common/Loader/Loader'

// #region TYPES
export interface GridRow {
  contract: ContractViewModel
  vehicleOrder?: VehicleOrderViewModel
  customer?: CustomerIndexViewModel
  replacedContract?: ContractViewModel
  futureContract?: ContractViewModel
  type: string
  costCenter: string
  userActionRequired: boolean
  orderFrozen: boolean
}

type DeliveryFilters = {
  orderStatus: ModalContractsDropdownFilter<GridRow>['filterFn']
  contract: InlineTextFilter<GridRow>['filterFn']
  costCenter: ModalContractsDropdownFilter<GridRow>['filterFn']
  replacedContractsRefNumber: InlineTextFilter<GridRow>['filterFn']
  makes: ModalContractsDropdownFilter<GridRow>['filterFn']
  model: InlineTextFilter<GridRow>['filterFn']
  driver: ModalDriverFilter<GridRow>['filterFn']
  gallikerReference: InlineTextFilter<GridRow>['filterFn']
  product: ModalContractsDropdownFilter<GridRow>['filterFn']
  nature: ModalContractsDropdownFilter<GridRow>['filterFn']
  actionRequired: InlineButtonFilter<GridRow>['filterFn']
}
// #endregion

export function Delivery(props: RouteComponentProps<{ companyId: string }>) {
  const [expiringDaysFilter] = useSettings('expiringDaysFilter')
  const savedIsReturnState = useMemo(() => {
    if (!FrontendSettings.get(FrontendSettingsKeys.isReturnActive)) return false

    return FrontendSettings.get(FrontendSettingsKeys.isReturnActive) === 'true' ? true : false
  }, [])

  const [returnsPage, setReturnsPage] = useState(savedIsReturnState ?? false)
  const [redirectedFromReturns, setRedirectedFromReturns] = useState(false)
  const [selectedCustomerKeys, setSelectedCustomersKeys] = useState<string[]>()
  const [selectedDriver, setSelectedDriver] = useState<DriverFilter>()
  const [alert, setAlert] = useState<string>()
  const [frozenWarning, setFrozenWarning] = useState<string>()
  const user = useAppSelector((state) => state.appData.currentUser)
  const isInterfleetPro = useIsInterfleetPro()
  const customerIdFromUrl = props.match.params.companyId
  const [calendarInfo, setCalendarInfo] = useState<GridRow>()
  const [calendarVisible, setCalendarVisible] = useState(false)
  const [combinedCalendarVisible, setCombinedCalendarVisible] = useState(false)

  const [previewCalendarData, setPreviewCalendarData] = useState<{
    open: boolean
    contractId: string | undefined
  }>({
    open: false,
    contractId: undefined,
  })

  const [detailsVisibility, setDetailsVisibility] = useState<Record<string, number | undefined>>({})
  const [contractHistory, setContractHistory] = useState<string>()

  // #region CUDSTOM HOOKS
  const { t, i18n } = useTranslation('common')
  const vehicleOrders = useGetVehicleOrdersQuery()
  const [getOrder] = useLazyGetOrderQuery()
  const allUnits = useGetAllUnitsQuery()
  const allTenants = useGetTenantsQuery()
  const allOrdersForms = useGetOrdersFormsQuery()
  const contracts = useGetContractsBySecurityKeysQuery(
    { securityKeys: selectedCustomerKeys! },
    { skip: !selectedCustomerKeys?.length }
  )
  const { customers: filteredCustomers } = useFilteredCustomers({
    status: 'ACTIVE',
  })

  const { setContractSupportRef } = useContractSupport()
  // #endregion

  // #region DATA MAPS
  const customerMap = useMemo(() => Arr.toHashMap(filteredCustomers ?? [], (c) => c.value!), [filteredCustomers])
  const unitsMap = useMemo(() => Arr.toHashMap(allUnits.data ?? [], (c) => c.value!), [allUnits.data])
  const vehicleOrdersMap = useMemo(() => Arr.toHashMap(vehicleOrders.data ?? [], (v) => v.id), [vehicleOrders.data])
  // #endregion

  // #region USE CALLBACKS
  const getCostCenterName = useCallback(
    (contract: ContractViewModel) => {
      if (unitsMap && contract.owner) {
        const relatedUnit = unitsMap[contract.owner]
        return relatedUnit && relatedUnit.unit ? relatedUnit.unit.name : t('NO_COST_CENTER')
      }
      return ''
    },
    [t, unitsMap]
  )

  const getReplacedContracts = useCallback(
    (contract: ContractViewModel) => {
      const futureContract = contracts?.currentData?.find((el) => {
        if (el?.replacedContractId === contract.id) {
          return el
        }
      })
      const replacedContract = contracts.currentData?.find((c) => c.id === contract.replacedContractId)
      return { futureContract, replacedContract }
    },
    [contracts.currentData]
  )

  const getDriverName = useCallback(
    (row: GridRow) => {
      const c = row.contract
      const driverName = c.vehicle?.driver?.displayName
      const value =
        !!driverName && !c.poolCar
          ? driverName
          : !c.poolCar && !driverName
          ? t('NOT_ASSIGNED')
          : c.poolCar && !!c.location
          ? `${t('POOL_CAR')} | ${c.location}`
          : c.poolCar && (!c.location || !!driverName)
          ? `${t('POOL_CAR')}`
          : ''
      return value
    },
    [t]
  )

  const downloadRequest = useCallback(
    async (orderId?: string) => {
      if (!orderId) {
        return
      }
      try {
        const order = await getOrder(orderId, true).unwrap()
        if (order) {
          RequestExportToPDF.startExport(order, allOrdersForms.data ?? [], i18n.language, t)
        }
      } catch {
        setAlert(t('REQUEST_IS_NOT_AVAILABLE_ANYMORE'))
      }
    },
    [allOrdersForms.data, getOrder, i18n.language, t]
  )

  const getOrderStatus = useCallback((row: GridRow) => {
    return row.vehicleOrder
      ? getVehicleOrderStatus(row.vehicleOrder)
      : defineContractStateNew({ contract: row.contract })
  }, [])

  const getCellClass = useCallback(
    (row: GridRow) => {
      const { vehicleOrder } = row
      const status = getOrderStatus(row)
      const maxDurationPassed = hasTimeElapsed(vehicleOrder?.earliestDelivery, 45)
      const isUndeliveredInternal = vehicleOrder?.isUndelivered && user?.isInternalUser
      const isExpiringConfirmation =
        vehicleOrder?.isConfirmationExpiring &&
        (user?.isInternalUser || !maxDurationPassed) &&
        vehicleOrder?.status !== VehicleOrderStatus.Delivered
      const orderAlert = isUndeliveredInternal || isExpiringConfirmation
      //!! status check should be moved to API. isConfirmationExpiring must return false when status = Delivered
      return orderAlert
        ? `contractStatus contractStatus-orderAlert orderAlert`
        : `contractStatus contractStatus-${status?.toLowerCase()} ${
            vehicleOrder && !maxDurationPassed && vehicleOrder.status === VehicleOrderStatus.PendingDeliveryConfirmation
              ? vehicleOrder.status
              : ''
          }`
    },
    [getOrderStatus, user?.isInternalUser]
  )

  const isOrderFrozen = useCallback((vehicleOrder?: VehicleOrderViewModel) => {
    return (
      !!vehicleOrder?.freeze &&
      hasTimeElapsed(vehicleOrder.freeze, 0) &&
      //!vehicleOrder.earliestDelivery && maybe needed
      vehicleOrder.status !== VehicleOrderStatus.Delivered &&
      vehicleOrder.status !== VehicleOrderStatus.Delivering
    )
  }, [])

  const isUserActionRequired = useCallback(
    (order?: VehicleOrderViewModel) => {
      const maxDurationPassed = hasTimeElapsed(order?.earliestDelivery, 45)
      const isPendingConfirmation =
        order?.status === VehicleOrderStatus.PendingDeliveryConfirmation && !maxDurationPassed
      const isUndeliveredInternal = order?.isUndelivered && user?.isInternalUser
      const isExpiring =
        order?.isConfirmationExpiring &&
        order?.status !== VehicleOrderStatus.Delivered &&
        (user?.isInternalUser || !maxDurationPassed)
      return (isPendingConfirmation || isUndeliveredInternal || isExpiring) ?? false
    },
    [user?.isInternalUser]
  )
  // #endregion
  // #region FilterFNS
  const filterFns: DeliveryFilters = useMemo(() => {
    return {
      orderStatus: (item, value) => {
        const status = getOrderStatus(item)
        if (status && value.includes(status)) {
          return true
        }
        return false
      },
      contract: (item, value) => !!item.contract.reference?.includes(value),
      costCenter: (item, value) => {
        const costCenter = getCostCenterName(item.contract)
        if (costCenter && value.includes(costCenter)) {
          return true
        }
        return false
      },
      replacedContractsRefNumber: (item, value) => {
        if (
          item.futureContract ||
          (item?.contract?.quoteReason === QuoteReason.Replacement && !!item?.contract.replacedContractId)
        ) {
          if (item.futureContract?.reference?.includes(value) || item.replacedContract?.reference?.includes(value)) {
            return true
          }
        }
        return false
      },
      makes: (item, value) => !!item.contract.vehicle?.makeName && value.includes(item.contract.vehicle.makeName),
      model: (item, value) =>
        !!item.contract.vehicle?.modelName &&
        item.contract.vehicle.modelName.toLowerCase().includes(value.toLowerCase()),
      driver: (item, value) => {
        const c = item.contract
        const driverName = c.vehicle?.driver?.displayName
        const poolcar = 'Poolcar'
        const notAssigned = 'Not assigned'

        const result =
          !!driverName && !c.poolCar
            ? driverName
            : !c.poolCar && !driverName
            ? notAssigned
            : c.poolCar && !!c.location
            ? `${poolcar} | ${c.location}`
            : c.poolCar && (!c.location || !!driverName)
            ? poolcar
            : ''
        const matchesDriver = value.drivers && !c.poolCar && !!driverName
        const matchesPoolcar = value.pool_cars && result.includes(poolcar)
        const matchesNotAssigned = value.not_assigned && result.includes(notAssigned)

        const matchesSearchString =
          !!value.searchString && result.toLowerCase().includes(value.searchString.toLowerCase())

        if (value.drivers && matchesDriver) {
          return matchesSearchString || !value.searchString
        }

        if (value.pool_cars && matchesPoolcar) {
          return matchesSearchString || !value.searchString
        }

        if (value.not_assigned && matchesNotAssigned) {
          return matchesSearchString || !value.searchString
        }

        if (!value.drivers && !value.pool_cars && !value.not_assigned) {
          return matchesSearchString
        }

        return false
      },
      gallikerReference: (item, value) => {
        const galReference: string = item.contract.vehicle?.['GallikerReference']
        if (galReference && galReference.toLowerCase().includes(value.toLowerCase())) {
          return true
        }
        return false
      },
      product: (item, value) => {
        const productId = item.contract.productId
        if (productId) {
          const product = productTypeIdMap.get(productId.toString())
          if (product && value.includes(product)) {
            return true
          }
        }
        return false
      },
      nature: (item, value) => !!item.contract.vehicle?.nature && value.includes(item.contract.vehicle?.nature),
      actionRequired: (item, value) => {
        return value ? item.userActionRequired : true
      },
    }
  }, [getCostCenterName, getOrderStatus])
  // #endregion

  useEffect(() => {
    if (customerIdFromUrl) {
      const customer = customerMap[`$${customerIdFromUrl.toUpperCase()}`]
      if (customer) setSelectedCustomersKeys([customer.value!])
    }
  }, [customerIdFromUrl, customerMap])
  // #region DataRows
  const { dataRows, totalActionRequired, totalFrozenOrders, showOrderWarningCount } = useMemo(() => {
    let totalActionRequired = 0
    let totalFrozenOrders = 0

    const initializedContracts = contracts.currentData?.filter(
      (c) => c.state === ContractState.Initialized || (c.state === ContractState.Running && c.requestId)
    )
    const dataRows: GridRow[] =
      initializedContracts?.map((contract) => {
        const { replacedContract, futureContract } = getReplacedContracts(contract)
        const customerKey = contract.owner?.split('//')[0]
        const customer = customerKey ? customerMap[customerKey] : undefined
        const costCenter = getCostCenterName(contract)

        const vehicleOrder = contract.requestId ? vehicleOrdersMap[contract.requestId] : undefined
        const orderFrozen = isOrderFrozen(vehicleOrder)
        const userActionRequired = orderFrozen ? false : isUserActionRequired(vehicleOrder)

        if (userActionRequired) totalActionRequired++
        if (orderFrozen) totalFrozenOrders++

        return {
          contract,
          vehicleOrder,
          replacedContract,
          futureContract,
          customer,
          costCenter,
          type: 'ROW',
          userActionRequired,
          orderFrozen,
        }
      }) ?? []
    const showOrderWarningCount = totalActionRequired > 0 || totalFrozenOrders > 0
    return { dataRows, totalActionRequired, totalFrozenOrders, showOrderWarningCount }
  }, [
    contracts.currentData,
    vehicleOrdersMap,
    getReplacedContracts,
    getCostCenterName,
    isUserActionRequired,
    customerMap,
    isOrderFrozen,
  ])

  const selectorOptionsList = useMemo(() => {
    const uniqueStatuses = new Set<string>()
    const uniqueNatures = new Set<string>()
    const uniqueCostCenters = new Set<string>()
    const uniqueMakes = new Set<string>()
    const uniqueProducts = new Set<string>()

    dataRows.forEach((row) => {
      const vehicleOrderStatus = getOrderStatus(row)
      if (vehicleOrderStatus) {
        uniqueStatuses.add(vehicleOrderStatus)
      }
      const costCenter = getCostCenterName(row.contract)
      if (costCenter) {
        uniqueCostCenters.add(costCenter)
      }
      if (row.contract.vehicle?.nature) {
        uniqueNatures.add(row.contract.vehicle.nature)
      }
      if (row.contract.vehicle?.makeName) {
        uniqueMakes.add(row.contract.vehicle?.makeName)
      }
      if (row.contract.productId) {
        const product = productTypeIdMap.get(row.contract.productId.toString())
        if (product) {
          uniqueProducts.add(product)
        }
      }
    })

    return {
      status: [...uniqueStatuses].sort(),
      nature: [...uniqueNatures].sort(),
      costCenters: [...uniqueCostCenters].sort(),
      makes: [...uniqueMakes].sort(),
      products: [...uniqueProducts].sort(),
    }
  }, [dataRows, getCostCenterName, getOrderStatus])

  // #endregion

  function displayBasicValue(value: string, customTitle?: string) {
    return <span title={customTitle || value}>{value}</span>
  }

  const handleActionClick = useCallback(
    (e: React.MouseEvent, formDisabled?: boolean) => {
      if (formDisabled) {
        e.preventDefault()
        setAlert(t('FROZEN_CONTRACT_REQUEST_WARNING'))
      }
    },
    [t]
  )

  const openContractSupport = useCallback(
    (reference?: string, frozen?: boolean) => {
      setContractSupportRef(frozen ? [reference ?? '', t('FROZEN_CONTRACT_SUPPORT_PLACEHOLDER')] : reference ?? '')
    },
    [setContractSupportRef, t]
  )

  // #region Columns
  const columns = useMemo((): GridColumn<GridRow>[] => {
    const disableClearBtn =
      (filteredCustomers?.length === 0 && selectedCustomerKeys?.length === 1) ||
      (filteredCustomers?.length === 1 && filteredCustomers[0].value === selectedCustomerKeys?.[0])
    const openCalendar = (row: GridRow, combined?: boolean) => {
      const { contract, vehicleOrder } = row
      if (!contract.requestId && !vehicleOrder) {
        return
      }
      setCalendarInfo(row)
      if (combined) {
        setCombinedCalendarVisible(true)
        setCalendarVisible(false)
      } else {
        setCombinedCalendarVisible(false)
        setCalendarVisible(true)
      }
    }
    const handlePreviewData = (row: GridRow) => {
      if (previewCalendarData.open) setPreviewCalendarData({ open: false, contractId: undefined })
      else setPreviewCalendarData({ open: true, contractId: row.contract.id })
    }
    const canPreviewData = (row: GridRow) => {
      return previewCalendarData.open && previewCalendarData.contractId === row.contract.id ? true : false
    }
    const expirationDays = Number(expiringDaysFilter)
    const cols: GridColumn<GridRow>[] = [
      {
        key: ColKeysEnum.company,
        name: t('COMPANY'),
        width: 320,
        resizable: false,
        filter: {
          type: 'modal_delivery_customer_filter',
          data: filteredCustomers,
          format: (value) => value?.map((c) => customerMap[c]?.customer?.name ?? ''),
          onChange: (value) => {
            const validKeys = value?.filter((c) => customerMap[c])
            setSelectedCustomersKeys(validKeys?.length ? validKeys : undefined)
          },
          value: selectedCustomerKeys,
          readOnly: disableClearBtn,
          cascadeClear: ColKeysEnum.costCenter,
          autoTrigger: !customerIdFromUrl,
          ignoreSaved: !!customerIdFromUrl || redirectedFromReturns,
        },
        renderCell: ({ row }) => displayBasicValue(row.customer?.customer?.name ?? ''),
        cellClass: getCellClass,
        exportFormat: (row) => row.customer?.customer?.name,
        sortBy: (row) => row.customer?.customer?.name,
      },
      {
        key: ColKeysEnum.costCenter,
        name: t('COST_CENTER'),
        width: 190,
        resizable: false,
        filter: {
          type: 'modal_contracts_dropdown_filter',
          title: t('COST_CENTER_SEARCH'),
          filterFn: filterFns.costCenter,
          data: selectorOptionsList.costCenters,
        },
        renderCell: ({ row }) => (
          <ContractCostCenterCell
            contract={row.contract}
            units={unitsMap}
            onHistoryToggle={(id) => setContractHistory(id)}
            showHistory={contractHistory === row.contract.id}
          />
        ),
        cellClass: getCellClass,
        exportFormat: (row) => getCostCenterName(row.contract),
        sortBy: (row) => getCostCenterName(row.contract),
      },
      {
        key: ColKeysEnum.nature,
        name: t('OBJ_TYPE'),
        width: 90,
        resizable: false,
        filter: {
          type: 'modal_contracts_dropdown_filter',
          title: t('OBJ_TYPE'),
          filterFn: filterFns.nature,
          data: selectorOptionsList.nature,
          format: (items) => items?.map((v) => t(`${v.toUpperCase()}`)),
        },
        renderCell({ row }) {
          if (row.contract.vehicle?.nature) {
            return <ObjTypeCell nature={row.contract.vehicle?.nature} />
          }
          return null
        },
        cellClass: getCellClass,
        exportFormat: (row) => row.contract.vehicle?.nature,
        sortBy: (row) => row.contract.vehicle?.nature,
      },
      {
        key: ColKeysEnum.contract,
        name: t('CONTRACT'),
        width: 140,
        resizable: false,
        filter: {
          type: 'inline_text_filter',
          filterFn: filterFns.contract,
        },
        renderCell({ row }) {
          const quickActionForms =
            allOrdersForms.currentData?.filter((f) =>
              Orders.isFormAvailableForContractQuickAction(
                f,
                row.contract,
                expirationDays,
                user,
                allTenants.currentData,
                filteredCustomers
              )
            ) ?? []
          const quickActions = [
            {
              form: undefined,
              label: t('SUPPORT_CONTRACT'),
            },
            ...quickActionForms.map((f) => {
              return { form: f, label: Orders.getFormTitle(f, i18n.language) }
            }),
          ].sort((a, b) => a.label.localeCompare(b.label))
          const frozen = row.orderFrozen
          return (
            <>
              <NavLink to={`/orderAndDelivery/details/${row.contract.id}`}>{row.contract.reference}</NavLink>
              {quickActions.length > 0 && (
                <div className="quickActions">
                  <div className="actionTrigger">
                    <i className="icon-actions" />
                    <i className="icon-bottom" />
                  </div>

                  <ul className="quickActionsList">
                    {quickActions.map((f, idx) => {
                      const formDisabled = frozen && f.form && f.form.orderType !== OrderType.ContractChange

                      return !f.form ? (
                        <li key={'contractIssue'}>
                          <a
                            onClick={() =>
                              frozen
                                ? setFrozenWarning(row.contract.reference)
                                : openContractSupport(row.contract.reference, frozen)
                            }
                          >
                            <i className={'icon-help'} /> {t('SUPPORT_CONTRACT')}
                          </a>
                        </li>
                      ) : (
                        <li key={idx}>
                          <NavLink
                            to={`/requests/new/${f.form.id}/contract/${row.contract.id}`}
                            exact={true}
                            onClick={(e) => handleActionClick(e, formDisabled)}
                          >
                            <i className={f.form.icon || 'icon-order'} />{' '}
                            {getFormTitle(f.form, i18n.language) || f.form.id}
                          </NavLink>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              )}
            </>
          )
        },
        cellClass: getCellClass,
        exportFormat: (row) => row.contract.reference,
        sortBy: (row) => row.contract.reference,
      },
      {
        key: ColKeysEnum.replacedContractsRefNumber,
        name: (
          <i
            title={t('REPLACED_CONTRACT')}
            className={'icon-change-car'}
            style={{ fontSize: '18px', verticalAlign: 'sub', margin: '0px 5px' }}
          />
        ),
        label: t('REPLACED_CONTRACT'),
        filter: {
          type: 'inline_text_filter',
          filterFn: filterFns.replacedContractsRefNumber,
        },
        width: 60,
        resizable: false,
        renderCell({ row }) {
          //!CHECK THIS LOGIC
          // TODO: extract to component
          const replacement = row.contract.quoteReason === QuoteReason.Replacement && row.contract.replacedContractId
          const contractId = replacement ? row.contract.replacedContractId : row.futureContract?.id
          const reference = replacement ? row.replacedContract?.reference : row.futureContract?.reference
          return (
            contractId && (
              <div
                style={{
                  display: 'flex',
                  width: '100%',
                  height: '100%',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <NavLink
                  style={{ color: '#39B54A', fontSize: '20px' }}
                  title={reference ?? ''}
                  className="icon-change-car"
                  to={`/${
                    row.futureContract && row.futureContract?.state === 'Initialized'
                      ? 'orderAndDelivery'
                      : 'generalContracts'
                  }/details/${contractId}`}
                />
              </div>
            )
          )
        },
        cellClass: getCellClass,
        exportFormat: (row) => row.futureContract?.reference || row.replacedContract?.reference,
        sortBy: (row) => {
          if (
            row.replacedContract ||
            (row?.contract?.quoteReason === QuoteReason.Replacement && !!row?.contract.replacedContractId)
          ) {
            return row.futureContract?.reference || row.replacedContract?.reference
          }
        },
      },
      {
        key: ColKeysEnum.requestLink,
        name: t('REQUEST_LINK'),
        width: 120,
        filter: {
          position: totalActionRequired > 0 ? 'header-center' : 'header-center',
          type: 'inline_button_filter',
          filterFn: filterFns.actionRequired,
          icon: 'icon-warning',
          conditionals: {
            renderRight: true,
            off: { text: t('ONLY_ACTION_REQUIRED'), icon: 'icon-show', class: 'actionRequired inactive' },
            on: { text: t('SHOW_ALL'), icon: 'icon-hide', class: 'actionRequired active' },
          },
          customClass: 'actionRequired',
        },
        renderCell({ row }) {
          if (row?.contract.requestId) {
            return (
              <div className="requestLink">
                <NavLink className="icon-requests" to={`/requests/${row.contract.requestId}`} />
                <i className="icon-download" onClick={() => downloadRequest(row.contract.requestId)} />
              </div>
            )
          } else {
            return null
          }
        },
        cellClass: getCellClass,
        sortBy: (row) => row.contract.requestId,
      },
      {
        key: ColKeysEnum.orderStatus,
        name: t('ORDER_STATUS'),
        width: 150,
        resizable: false,
        filter: {
          type: 'modal_contracts_dropdown_filter',
          title: t('ORDER_STATUS_SEARCH'),
          filterFn: filterFns.orderStatus,
          data: selectorOptionsList.status,
          format: (items) => items?.map((v) => t(`${v.toUpperCase()}`)),
        },
        renderCell({ row }) {
          const vehicleOrderStatus = getOrderStatus(row)
          return vehicleOrders.data ? (
            <>
              <label
                className={`contractListStatus statusArea ${vehicleOrderStatus?.toLowerCase()}`}
                title={t(`${vehicleOrderStatus}`.toUpperCase())}
              >
                {t(`${vehicleOrderStatus}`.toUpperCase())}
              </label>
              {row.contract.requestId && row.vehicleOrder && row.vehicleOrder.status && (
                <CellExpanderFormatter
                  expanded={false}
                  level={1}
                  onCellExpand={() => {
                    setDetailsVisibility((current) => {
                      return {
                        ...current,
                        [row.contract.id]: current[row.contract.id] ? undefined : 200,
                      }
                    })
                  }}
                  status={row.vehicleOrder.status}
                  isConfirmationExpiring={row.vehicleOrder?.isConfirmationExpiring ?? false}
                  isUndelivered={row.vehicleOrder?.isUndelivered ?? false}
                  isInternalUser={user?.isInternalUser ?? false}
                  earliestDelivery={row.vehicleOrder.earliestDelivery}
                  freeze={row.vehicleOrder.freeze}
                />
              )}
            </>
          ) : (
            <div style={{ display: 'flex', margin: 'auto' }}>
              <LoadingSpinner />
            </div>
          )
        },
        cellClass: getCellClass,
        exportFormat: getOrderStatus,
        sortBy: getOrderStatus,
      },
      {
        key: ColKeysEnum.vehicle,
        width: 80,
        name: t('MAKES'),
        label: t('MAKES_AND_MODEL'),
        resizable: false,
        filter: {
          type: 'modal_contracts_dropdown_filter',
          title: t('MAKES'),
          filterFn: filterFns.makes,
          data: selectorOptionsList.makes,
        },
        renderCell({ row }) {
          const vehicle = row.contract.vehicle
          if (vehicle?.makeName && vehicle?.makeName.length >= 2) {
            const logos =
              vehicle?.makeName.toLowerCase().indexOf('ladestation') > -1
                ? 'dummy'
                : vehicle?.makeName.replace(/\s+/g, '-').toLowerCase()
            return (
              <div className="vehicleDescription">
                {logos && (
                  <img
                    src={`../../../../BW/${logos.normalize('NFD').replace(/[\u0300-\u036f]/g, '')}.svg`}
                    width="23"
                    height="23"
                  />
                )}
                {vehicle.description && displayBasicValue(vehicle.description)}
              </div>
            )
          }
          return null
        },
        cellClass: getCellClass,
        exportFormat: (row) => row.contract.vehicle?.description,
        sortBy: (row) => row.contract.vehicle?.makeName,
      },
      {
        key: ColKeysEnum.model,
        name: t('MODEL'),
        minWidth: 180,
        resizable: false,
        parentKey: ColKeysEnum.vehicle,
        filter: { type: 'inline_text_filter', filterFn: filterFns.model },
        sortBy: (row) => row.contract.vehicle?.description,
      },
      {
        key: ColKeysEnum.driverName,
        name: t('DRIVER_NAME'),
        width: 150,
        minWidth: 150,
        resizable: false,
        filter: {
          type: 'modal_driver_filter',
          filterFn: filterFns.driver,
          format: (value) => {
            if (!value) {
              return
            }
            const result = []
            if (value.pool_cars) result.push(t('POOL_CARS'))
            if (value.not_assigned) result.push(t('NOT_ASSIGNED'))
            if (value.drivers) result.push(t('DRIVERS'))
            if (value.searchString) result.push(value.searchString)
            return result
          },
          value: selectedDriver,
          onChange: (value) => setSelectedDriver(value),
        },
        renderCell: ({ row }) => displayBasicValue(getDriverName(row)),
        cellClass: getCellClass,
        exportFormat: getDriverName,
        sortBy: getDriverName,
      },
      {
        key: ColKeysEnum.gallikerReference,
        name: t('LOGISTICID'),
        width: 100,
        minWidth: 150,
        resizable: false,
        filter: {
          type: 'inline_text_filter',
          filterFn: filterFns.gallikerReference,
        },
        //! EXTRA WONT BE USED ANYMORE, REMOVE BEFORE FULL RELEASE
        renderCell: ({ row }) => displayBasicValue(row.contract.vehicle?.['GallikerReference']),
        cellClass: getCellClass,
        exportFormat: (row) => row.contract.vehicle?.['GallikerReference'],
        sortBy: (row) => row.contract.vehicle?.['GallikerReference'],
      },
      {
        key: ColKeysEnum.product,
        name: t('PRODUCT'),
        width: 100,
        resizable: false,
        filter: {
          type: 'modal_contracts_dropdown_filter',
          title: t('PRODUCT_SEARCH'),
          transPrefix: 'PRODUCT_SHORT_',
          filterFn: filterFns.product,
          data: selectorOptionsList.products ?? [],
        },
        renderCell({ row }) {
          const productId = row.contract.productId
          if (productId) {
            const product = productTypeIdMap.get(productId.toString())
            if (product) {
              return displayBasicValue(
                t(`PRODUCT_SHORT_${product}`.toUpperCase()),
                t(`PRODUCT_FULL_${product}`.toUpperCase())
              )
            }
          }
          return null
        },
        cellClass: getCellClass,
        exportFormat(row) {
          if (row.contract.productId) {
            const product = productTypeIdMap.get(row.contract.productId.toString())
            if (product) {
              return t(`PRODUCT_FULL_${product}`.toUpperCase())
            }
          }
        },
        sortBy: (row) => {
          if (row.contract.productId) {
            const product = productTypeIdMap.get(row.contract.productId.toString())
            if (product) {
              return t(`PRODUCT_FULL_${product}`.toUpperCase())
            }
          }
        },
      },
      {
        key: ColKeysEnum.startDate,
        name: t('START_DATE'),
        width: 100,
        resizable: false,
        renderCell: ({ row }) => printDate(row.contract.startDate),
        cellClass: getCellClass,
        exportFormat: (row) => (isDateValid(row.contract.startDate) ? printDate(row.contract.startDate) : undefined),
        sortBy: (row) => (isDateValid(row.contract.startDate) ? new Date(row.contract.startDate) : undefined),
      },
      {
        key: ColKeysEnum.requestDelivery,
        name: t('EARLIEST_DELIVERY'),
        width: 215,
        resizable: false,
        renderCell({ row }) {
          return <span>{!!row.vehicleOrder?.earliestDelivery && printDate(row.vehicleOrder?.earliestDelivery)}</span>
        },
        cellClass: getCellClass,
        exportFormat: (row) =>
          isDateValid(row.vehicleOrder?.earliestDelivery) ? printDate(row.vehicleOrder?.earliestDelivery) : undefined,
        sortBy: (row) =>
          row.vehicleOrder?.earliestDelivery && isDateValid(row.vehicleOrder?.earliestDelivery)
            ? new Date(row.vehicleOrder?.earliestDelivery)
            : undefined,
      },
      {
        key: ColKeysEnum.entryDate,
        name: t('CONFIRMED_DELIVERY'),
        width: 180,
        resizable: false,
        renderCell({ row }) {
          const entryDate = row.vehicleOrder?.confirmedDelivery
          if (entryDate) {
            const address = row.vehicleOrder?.confirmedDeliveryAddress
            const contact = row.vehicleOrder?.confirmedDeliveryContact
            const tires = row.vehicleOrder?.confirmedTireDeliveryAddress

            return (
              <span className="confirmedDelivery">
                <i className="icon-roles" /> {printDate(entryDate)}
                <i className="icon-marker" onClick={() => handlePreviewData(row)} />
                {canPreviewData(row) && <ConfirmedDataTooltip address={address} contact={contact} tires={tires} />}
              </span>
            )
          }
          const earliestDeliveryDate = row.vehicleOrder?.earliestDelivery
          const notDelivered = row.vehicleOrder?.status !== VehicleOrderStatus.Delivered
          const frozen = row.orderFrozen

          if (notDelivered && (earliestDeliveryDate || frozen)) {
            const now = Date.now()
            const requestTime = new Date(earliestDeliveryDate ?? 0).getTime()
            const timeWindow = 45 * 24 * 60 * 60 * 1000
            const validTimeWindow = now <= requestTime || now - requestTime <= timeWindow

            if (validTimeWindow || frozen) {
              const showCombinedView = validTimeWindow && !!row.replacedContract && !!row.vehicleOrder?.returnLogisticId
              // const showCombinedView = validTimeWindow && true
              return (
                <span>
                  {showCombinedView ? (
                    <button className="btnDate" onClick={() => openCalendar(row, true)}>
                      <i className={'icon-calendar'} />
                      {t('SELECT_DATE')}
                    </button>
                  ) : (
                    <button className="btnDate" onClick={() => openCalendar(row)}>
                      <i className={frozen ? 'icon-marker' : 'icon-calendar'} />
                      {frozen ? t('SELECT_LOCATION') : t('SELECT_DATE')}
                    </button>
                  )}
                </span>
              )
            }
          }
        },
        cellClass: getCellClass,
        exportFormat: (row) =>
          isDateValid(row.vehicleOrder?.confirmedDelivery) ? printDate(row.vehicleOrder?.confirmedDelivery) : undefined,
        sortBy: (row) =>
          row.vehicleOrder?.confirmedDelivery && isDateValid(row.vehicleOrder?.confirmedDelivery)
            ? new Date(row.vehicleOrder?.confirmedDelivery)
            : undefined,
      },
      {
        key: ColKeysEnum.endDate,
        name: t('CONTRACT_END_DATE'),
        width: 90,
        resizable: false,
        renderCell: ({ row }) => printDate(row.contract.endDate),
        cellClass: getCellClass,
        exportFormat: (row) => (isDateValid(row.contract.endDate) ? printDate(row.contract.endDate) : undefined),
        sortBy: (row) => (isDateValid(row.contract.endDate) ? new Date(row.contract.endDate) : undefined),
      },
    ]
    return cols
  }, [
    filteredCustomers,
    selectedCustomerKeys,
    t,
    customerIdFromUrl,
    redirectedFromReturns,
    getCellClass,
    filterFns,
    selectorOptionsList,
    getOrderStatus,
    selectedDriver,
    getDriverName,
    previewCalendarData.open,
    previewCalendarData.contractId,
    customerMap,
    unitsMap,
    contractHistory,
    getCostCenterName,
    i18n.language,
    downloadRequest,
    vehicleOrders.data,
    totalActionRequired,
    allOrdersForms.currentData,
    allTenants.currentData,
    user,
    handleActionClick,
    openContractSupport,
    expiringDaysFilter,
  ])
  // #endregion

  // #region FX
  const handleReturnRedirect = useCallback((isReturn: boolean) => {
    setReturnsPage(isReturn)
    setRedirectedFromReturns(!isReturn)
    FrontendSettings.set(FrontendSettingsKeys.isReturnActive, `${isReturn}`)
  }, [])

  /**Sets isReturn local storage value to false, when leaving Returns / Delivery page
   */
  useEffect(() => {
    return () => {
      FrontendSettings.set(FrontendSettingsKeys.isReturnActive, `false`)
    }
  }, [])

  const handleReturnCompaniesSelection = useCallback((companies?: string[]) => {
    setRedirectedFromReturns(true)
    setSelectedCustomersKeys(companies)
  }, [])

  // #endregion

  // #region JSX
  const renderDetails = useCallback(
    (vehicleOrder?: VehicleOrderViewModel) => {
      return <OrderDetails vehicleOrderData={vehicleOrder} isInternalUser={user?.isInternalUser || false} />
    },
    [user?.isInternalUser]
  )
  return (
    <Loader className="grid-size" blocking={vehicleOrders.isFetching || contracts.isFetching || !filteredCustomers}>
      <div className="headerSwitch">
        <div className={`headerItem ${returnsPage ? '' : `active`}`} onClick={() => handleReturnRedirect(false)}>
          <i className="icon-garageOut" /> {t('ORDER_AND_DELIVERY')}
          {
            <OrderWarningCount
              showOrderWarningCount={showOrderWarningCount}
              totalActionRequired={totalActionRequired}
              totalFrozenOrders={totalFrozenOrders}
            />
          }
        </div>
        <div
          className={`headerItem ${returnsPage ? 'active' : user?.isInternalUser || isInterfleetPro ? `` : 'disabled'}`}
          onClick={() => (user?.isInternalUser || isInterfleetPro ? handleReturnRedirect(true) : undefined)}
          title="Coming soon"
        >
          <i className="icon-garageIn" /> {t('RETURN')}
        </div>
      </div>
      {!returnsPage ? (
        <Grid
          id="Delivery"
          columns={columns}
          rows={dataRows}
          rowKeyGetter={(item) => {
            return item.contract.id
          }}
          pagination={true}
          showTotalItems={true}
          renderDetails={(row) => renderDetails(row.vehicleOrder)}
          detailsVisibility={detailsVisibility}
          onReload={
            selectedCustomerKeys?.length
              ? () => {
                  vehicleOrders.refetch()
                  contracts.refetch()
                }
              : undefined
          }
          customClass="ordersDelivery"
          exportTitle="OrderAndDelivery"
        />
      ) : (
        <Returns companies={selectedCustomerKeys} updateDeliveryCompanySelection={handleReturnCompaniesSelection} />
      )}
      {calendarVisible && calendarInfo && (
        <SchedulerModal
          visible={calendarVisible}
          onClose={() => {
            setCalendarVisible(false)
            setCalendarInfo(undefined)
          }}
          dataRow={calendarInfo}
          freeze={calendarInfo.vehicleOrder?.freeze}
        />
      )}
      {combinedCalendarVisible && calendarInfo && (
        <CombinedScheduler
          visible={combinedCalendarVisible}
          onClose={() => {
            setCalendarVisible(false)
            setCalendarInfo(undefined)
          }}
          dataRow={calendarInfo}
          getCostCenterName={getCostCenterName}
        />
      )}
      <Alert visible={!!alert} onClose={() => setAlert(undefined)} title={t('WARNING')} message={alert ?? ''} />
      <Alert
        visible={!!frozenWarning}
        onClose={() => {
          setFrozenWarning(undefined)
          openContractSupport(frozenWarning, true)
        }}
        title={t('WARNING')}
        message={t('FROZEN_CONTRACT_REQUEST_WARNING')}
      />
    </Loader>
  )
}
